import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { StressEventContext } from '../lib/predictiveSignals'

export function usePredictiveCopy() {
  const { t } = useTranslation()

  const signalLabel = useCallback(
    (signalType: string) => t(`predictive.signals.${signalType}`, { defaultValue: signalType }),
    [t],
  )

  const stressTierLabel = useCallback(
    (tier: StressEventContext['stressTier']) => t(`predictive.stressTier.${tier}`),
    [t],
  )

  const formatEventCountdown = useCallback(
    (hoursUntilEvent: number) => {
      if (hoursUntilEvent < 1) return t('predictive.countdown.withinHour')
      if (hoursUntilEvent < 24) {
        const rounded = Math.round(hoursUntilEvent)
        return rounded === 1
          ? t('predictive.countdown.hours', { count: rounded })
          : t('predictive.countdown.hours_plural', { count: rounded })
      }
      const days = Math.floor(hoursUntilEvent / 24)
      const hours = Math.round(hoursUntilEvent % 24)
      if (hours === 0) {
        return days === 1
          ? t('predictive.countdown.days', { count: days })
          : t('predictive.countdown.days_plural', { count: days })
      }
      return t('predictive.countdown.daysHours', { days, hours })
    },
    [t],
  )

  const stressEventCoachSummary = useCallback(
    (context: StressEventContext) => {
      const timing = formatEventCountdown(context.hoursUntilEvent).toLowerCase()
      const tier = stressTierLabel(context.stressTier).toLowerCase()

      if (context.interventionMode === 'maintain') {
        let learned = ''
        if (context.learningApplied && context.personalHrvImpactPct != null) {
          learned = t('predictive.summary.maintainLearned', { pct: context.personalHrvImpactPct })
        } else if (context.effectiveCopingStyle === 'movement_first') {
          learned = t('predictive.summary.maintainPreference')
        }
        return t('predictive.summary.maintain', {
          title: context.eventTitle,
          tier,
          timing,
          learned,
        })
      }

      const learned =
        context.learningApplied && context.personalHrvImpactPct != null
          ? t('predictive.summary.deloadLearned', { pct: context.personalHrvImpactPct })
          : ''
      return t('predictive.summary.deload', {
        title: context.eventTitle,
        tier,
        timing,
        learned,
      })
    },
    [formatEventCountdown, stressTierLabel, t],
  )

  const confidenceLearningHint = useCallback(
    (context: StressEventContext) => {
      if (context.personalSampleCount < 2) return t('predictive.confidenceHint.low')
      return t('predictive.confidenceHint.personalized', { count: context.personalSampleCount })
    },
    [t],
  )

  return {
    signalLabel,
    stressTierLabel,
    formatEventCountdown,
    stressEventCoachSummary,
    confidenceLearningHint,
    t,
  }
}
